import { create } from "zustand";
import { AppConfig } from "@/common/appConfig";
import { MicDeviceType } from "@/common/enums/enums";
import {
  NoiseReduction,
  TranscriptionNoRealtimeProviderName,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import type { MicDevice } from "@/hooks/useAudioInputDevices";
import { getRecordingMicrophoneId } from "@/lib/recordingMicrophone";
import { useAppSettingsStore } from "./appSettings.store";

export interface RecordingSettingsState {
  inputLanguage: string;
  noiseReduction: NoiseReduction;
  transcriptionProvider: TranscriptionProviderName;
  transcriptionNoRealtimeProvider: TranscriptionNoRealtimeProviderName;
  saveInputLanguage: (value: string) => void;
  saveNoiseReduction: (value: NoiseReduction) => void;
  saveTranscriptionProvider: (value: TranscriptionProviderName) => void;
  saveTranscriptionNoRealtimeProvider: (
    value: TranscriptionNoRealtimeProviderName,
  ) => void;
  isRealtimeEnabled: boolean;
  saveIsRealtimeEnabled: (value: boolean) => void;
  noiseSuppression: boolean;
  saveNoiseSuppression: (value: boolean) => void;
  diarize: boolean;
  saveDiarize: (value: boolean) => void;
  micDevice: MicDevice | null;
  saveMicDevice: (value: MicDevice | null) => void;
  /**
   * The microphone this computer records from, read when a recording starts:
   * the one chosen in New visit, else the one remembered from an earlier
   * visit (Start visit and Add recording skip the New visit screen), else
   * null for the default one. A linked phone records on the phone, so this
   * computer uses its default microphone.
   */
  getRecordingDeviceId: () => string | null;
}

// With speaker roles, diarization is on by default for both live and recorded
// audio wherever the provider supports it (Deepgram); the clinician can turn
// it off. OpenAI live transcription cannot diarize and ignores the setting.
// Without the flag: off for live, on for recorded audio (unchanged).
const diarizeByDefault = (isRealtimeEnabled: boolean): boolean =>
  AppConfig.enableSpeakerRoles || !isRealtimeEnabled;

export const useRecordingSettingsStore = create<RecordingSettingsState>()(
  (set, get) => ({
    // TODO: get from user preference
    inputLanguage: useAppSettingsStore.getState().inputLanguage,
    noiseReduction: NoiseReduction.FAR_FIELD,
    transcriptionProvider: TranscriptionProviderName.OPENAI,
    isRealtimeEnabled: true,
    noiseSuppression: false,
    diarize: diarizeByDefault(true),
    micDevice: null,
    syncMicrophone: true,
    transcriptionNoRealtimeProvider:
      TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
    saveInputLanguage: (value: string): void => {
      set({
        inputLanguage: value,
      });
    },
    saveNoiseReduction: (value: NoiseReduction): void => {
      set({
        noiseReduction: value,
      });
    },
    saveTranscriptionProvider: (value: TranscriptionProviderName): void => {
      set({
        transcriptionProvider: value,
      });
    },
    saveIsRealtimeEnabled: (value: boolean): void => {
      set({
        isRealtimeEnabled: value,
        diarize: diarizeByDefault(value),
      });
    },
    saveNoiseSuppression: (value: boolean): void => {
      set({
        noiseSuppression: value,
      });
    },
    saveDiarize: (value: boolean): void => {
      set({
        diarize: value,
      });
    },
    saveMicDevice: (value: MicDevice | null): void => {
      set({
        micDevice: value,
      });
    },
    getRecordingDeviceId: (): string | null => {
      const device = get().micDevice;
      if (device?.type === MicDeviceType.MOBILE) return null;
      if (device) return device.value || null;
      return getRecordingMicrophoneId();
    },
    saveTranscriptionNoRealtimeProvider: (
      value: TranscriptionNoRealtimeProviderName,
    ): void => {
      set({
        transcriptionNoRealtimeProvider: value,
      });
    },
  }),
);
